import { useMemo, useRef } from "react";
import { columnWidth, nextSort, resolveColumns, sortRows, stickyCount, withSort, withWidth } from "./tablePrefs";

/**
 * Table whose columns, order, widths and sort are chosen by each user (Чат 4, правка №15).
 * [Claude] 2026-11-02. Generic: also meant for candidates as a table (правка №14, Чат 1).
 *
 * props: catalog (column defs, see tablePrefs.js), defaultColumns (ids), rows, rowKey(row),
 *        prefs + onPrefsChange (from useTablePrefs), onRowClick(row), actions(row) — last fixed
 *        column, extraColumns — non-configurable columns appended before actions, empty (node).
 * Columns up to the pinned one (ФИО) stick to the left while scrolling sideways; the header sticks to the top.
 */
const ACTIONS_WIDTH = 150;

function ResizeHandle({ onResize, startWidth, label }) {
    const start = useRef(null);
    const onMouseDown = (event) => {
        event.preventDefault();
        event.stopPropagation();
        start.current = { x: event.clientX, w: startWidth };
        const move = (e) => onResize(start.current.w + e.clientX - start.current.x, false);
        const up = (e) => {
            window.removeEventListener("mousemove", move);
            window.removeEventListener("mouseup", up);
            onResize(start.current.w + e.clientX - start.current.x, true);
        };
        window.addEventListener("mousemove", move);
        window.addEventListener("mouseup", up);
    };
    return <span role="separator" aria-orientation="vertical" aria-label={`Ширина колонки «${label}»`} onMouseDown={onMouseDown}
        onClick={(e) => e.stopPropagation()}
        className="absolute right-0 top-0 h-full w-2 cursor-col-resize select-none hover:bg-amber-300/60" />;
}

export default function ConfigurableTable({
    catalog, defaultColumns, rows, rowKey = (row) => row.id, prefs, onPrefsChange,
    onRowClick, actions, extraColumns = [], empty, testId = "configurable-table",
}) {
    const columns = useMemo(() => [...resolveColumns(catalog, prefs, defaultColumns), ...extraColumns], [catalog, prefs, defaultColumns, extraColumns]);
    const sortColumn = prefs?.sort ? columns.find((c) => c.id === prefs.sort.key) : null;
    const sorted = useMemo(() => sortRows(rows, sortColumn, prefs?.sort?.dir), [rows, sortColumn, prefs]);

    const onSort = (column) => {
        if (column.sortable === false) return;
        onPrefsChange(withSort(prefs, nextSort(prefs?.sort, column.id)));
    };
    const onResize = (column, px) => {
        onPrefsChange(withWidth(prefs, column.id, px));
    };

    const widths = columns.map((c) => columnWidth(c, prefs));
    const sticky = stickyCount(columns);
    const lefts = widths.map((_, i) => widths.slice(0, i).reduce((a, b) => a + b, 0));
    const stickStyle = (i) => (i < sticky ? { left: lefts[i] } : undefined);
    const total = widths.reduce((a, b) => a + b, 0) + (actions ? ACTIONS_WIDTH : 0);

    return <div className="overflow-auto" style={{ maxHeight: "calc(100vh - 230px)" }} data-testid={testId}>
        <table className="border-separate border-spacing-0 text-sm" style={{ width: total, minWidth: "100%", tableLayout: "fixed" }}>
            <colgroup>
                {widths.map((w, i) => <col key={columns[i].id} style={{ width: w }} />)}
                {actions ? <col style={{ width: ACTIONS_WIDTH }} /> : null}
            </colgroup>
            <thead>
                <tr>
                    {columns.map((column, i) => {
                        const active = prefs?.sort?.key === column.id;
                        const arrow = active ? (prefs.sort.dir === "asc" ? "▲" : "▼") : "";
                        return <th key={column.id} scope="col" data-column={column.id}
                            aria-sort={active ? (prefs.sort.dir === "asc" ? "ascending" : "descending") : "none"}
                            className={`sticky top-0 border-b border-slate-200 bg-slate-50 px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 ${i < sticky ? "z-30" : "z-20"} ${column.sortable === false ? "" : "cursor-pointer hover:text-slate-800"}`}
                            style={stickStyle(i)} onClick={() => onSort(column)}>
                            <span className="block line-clamp-2 break-words pr-2 leading-tight" title={column.label}>{column.label} <span className="text-amber-600">{arrow}</span></span>
                            {!column.fixed ? <ResizeHandle label={column.label} startWidth={widths[i]} onResize={(px) => onResize(column, px)} /> : null}
                        </th>;
                    })}
                    {actions ? <th scope="col" className="sticky top-0 z-20 border-b border-slate-200 bg-slate-50 px-3 py-2" aria-label="Действия" /> : null}
                </tr>
            </thead>
            <tbody>
                {sorted.map((row) => <tr key={rowKey(row)} className="group" data-testid="configurable-table-row">
                    {columns.map((column, i) => <td key={column.id} style={stickStyle(i)}
                        onClick={onRowClick && !column.noRowClick ? () => onRowClick(row) : undefined}
                        className={`border-b border-slate-100 px-3 py-2 align-middle text-slate-700 group-hover:bg-amber-50/60 ${i < sticky ? "sticky z-10 bg-white" : "bg-white"} ${i === sticky - 1 ? "shadow-[1px_0_0_0_#e2e8f0]" : ""} ${onRowClick && !column.noRowClick ? "cursor-pointer" : ""}`}>
                        {/* Узкая колонка — текст переносится на 2–3 строки (правка №15, MR 4) */}
                        <div className={column.nowrap ? "truncate" : "line-clamp-3 break-words leading-snug"}>{column.render ? column.render(row) : (row[column.id] ?? "—")}</div>
                    </td>)}
                    {actions ? <td className="border-b border-slate-100 bg-white px-3 py-2 text-right group-hover:bg-amber-50/60">{actions(row)}</td> : null}
                </tr>)}
            </tbody>
        </table>
        {!sorted.length ? <div className="py-12 text-center text-sm text-slate-400">{empty}</div> : null}
    </div>;
}
